import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Container } from '../components/ui/Container'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  MessageCircle,
  Send,
  CheckCircle,
  ArrowRight,
  Heart,
  Users,
  Shield,
  Award,
  Star
} from 'lucide-react'

import PageLayout from '../components/layout/PageLayout'

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    preferredContact: 'email'
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    setIsSubmitting(false)
    setSubmitSuccess(true)
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setSubmitSuccess(false)
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        preferredContact: 'email'
      })
    }, 3000)
  }

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      details: ["dhruvaparik@gmail.com"],
      description: "General inquiries and questions"
    },
    {
      icon: Phone,
      title: "Call Us",
      details: ["+1 639 318 1891"],
      description: "Available by appointment"
    },
    {
      icon: MapPin,
      title: "Location",
      details: ["Hamilton, Ontario", "Canada"],
      description: "Remote-first company"
    },
    {
      icon: Clock,
      title: "Support Hours",
      details: ["Email support available 24/7"],
      description: "Response within 24-48 hours"
    }
  ]

  const reasonsToContact = [
    {
      icon: MessageCircle,
      title: "Pre-Sales Questions",
      description: "Learn more about our testing services and pricing options"
    },
    {
      icon: Heart,
      title: "Medical Consultations",
      description: "Discuss your biomarker results with our health experts"
    },
    {
      icon: Users,
      title: "Corporate Wellness",
      description: "Inquire about our enterprise health solutions"
    },
    {
      icon: Shield,
      title: "Privacy & Security",
      description: "Questions about data protection and HIPAA compliance"
    },
    {
      icon: Award,
      title: "Partnership Opportunities",
      description: "Explore collaboration and partnership possibilities"
    },
    {
      icon: Star,
      title: "Customer Support",
      description: "Get help with your account, orders, or technical issues"
    }
  ]


  return (
    <PageLayout>
      <div className="min-h-screen bg-theme-bg text-theme-text-primary">
        {/* Hero Section */}
        <section className="pt-24 pb-20 bg-theme-surface border-b border-theme-border">
          <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-theme-primary to-theme-secondary bg-clip-text text-transparent mb-6">
              Get in Touch
            </h1>
            <p className="text-xl md:text-2xl text-theme-secondary mb-8 leading-relaxed">
              Have questions about BioTune? We're here to help you optimize your health journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth/signup">
                <Button size="lg" variant="default">
                  Start Your Journey
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="#contact-form">
                <Button size="lg" variant="outline" className="border-blue-600 text-theme-text-primary hover:bg-theme-surface hover:scale-105 transition-all duration-300">
                  Send Message
                </Button>
              </a>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-theme-surface light-surface">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent mb-4">
              Contact Information
            </h2>
            <p className="text-xl text-theme-secondary max-w-3xl mx-auto">
              Multiple ways to reach our team for different types of inquiries.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                whileHover={{ y: -5 }}
                className="transition-all duration-300"
              >
                <Card className="h-full text-center bg-theme-surface light-surface border border-theme-border hover:shadow-xl transition-all duration-300 group">
                  <CardHeader>
                    <div className="w-16 h-16 bg-theme-surface light-surface rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                      <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                        <info.icon className="w-8 h-8 text-theme-primary" />
                      </motion.div>
                    </div>
                    <CardTitle className="text-xl text-theme-primary">{info.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-4">
                      {info.details.map((detail, detailIndex) => (
                        <p key={detailIndex} className="text-theme-secondary font-medium">{detail}</p>
                      ))}
                    </div>
                    <p className="text-sm text-theme-secondary">{info.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Reasons to Contact */}
      <section className="py-20 bg-theme-surface light-surface">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent mb-4">
              How Can We Help You?
            </h2>
            <p className="text-xl text-theme-secondary max-w-3xl mx-auto">
              Common reasons people reach out to our team.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reasonsToContact.map((reason, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                whileHover={{ y: -5 }}
                className="transition-all duration-300"
              >
                <Card className="h-full bg-theme-surface light-surface border border-theme-border hover:shadow-xl transition-all duration-300 group">
                  <CardHeader>
                    <div className="w-12 h-12 bg-theme-surface light-surface rounded-lg flex items-center justify-center mb-4 shadow-sm">
                      <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                        <reason.icon className="w-6 h-6 text-theme-primary" />
                      </motion.div>
                    </div>
                    <CardTitle className="text-lg text-theme-primary">{reason.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-theme-secondary">{reason.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 bg-theme-surface light-surface">
        <Container>
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-theme-primary to-theme-secondary bg-clip-text text-transparent mb-4">
                Send Us a Message
              </h2>
              <p className="text-xl text-theme-secondary">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>
            </motion.div>

            <Card className="bg-theme-surface border border-theme-border hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                {submitSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="text-center py-12"
                  >
                    <div className="w-16 h-16 glass-surface rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-theme-primary mb-2">Message Sent Successfully!</h3>
                    <p className="text-theme-secondary">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-theme-text-secondary mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-theme-border rounded-xl focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-300 hover:border-theme-border-hover bg-theme-surface text-theme-text-primary placeholder:text-theme-text-muted"
                          placeholder="Enter your full name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-theme-text-secondary mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-theme-border rounded-xl focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-300 hover:border-theme-border-hover bg-theme-surface text-theme-text-primary placeholder:text-theme-text-muted"
                          placeholder="Enter your email address"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-theme-text-secondary mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-theme-border rounded-xl focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-300 hover:border-theme-border-hover bg-theme-surface text-theme-text-primary placeholder:text-theme-text-muted"
                          placeholder="Enter your phone number"
                        />
                      </div>
                      <div>
                        <label htmlFor="preferredContact" className="block text-sm font-medium text-theme-text-secondary mb-2">
                          Preferred Contact Method
                        </label>
                        <select
                          id="preferredContact"
                          name="preferredContact"
                          value={formData.preferredContact}
                          onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-theme-border rounded-xl focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-300 hover:border-theme-border-hover bg-theme-surface text-theme-text-primary placeholder:text-theme-text-muted"
                        >
                          <option value="email">Email</option>
                          <option value="phone">Phone</option>
                          <option value="text">Text Message</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Subject *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-theme-border rounded-xl focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-300 hover:border-theme-border-hover bg-theme-surface text-theme-text-primary placeholder:text-theme-text-muted"
                      >
                        <option value="">Select a subject</option>
                        <option value="general">General Inquiry</option>
                        <option value="pricing">Pricing Questions</option>
                        <option value="technical">Technical Support</option>
                        <option value="medical">Medical Questions</option>
                        <option value="partnership">Partnership Opportunities</option>
                        <option value="media">Media Inquiries</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-theme-text-secondary mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-theme-border rounded-xl focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-300 hover:border-theme-border-hover bg-theme-surface text-theme-text-primary placeholder:text-theme-text-muted"
                        placeholder="Please describe your inquiry in detail..."
                      />
                    </div>

                    <div className="flex justify-center">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        variant="default"
                        size="lg"
                        className="min-w-[200px] transform hover:scale-105 active:scale-95 transition-all duration-300"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </Container>
      </section>
      </div>
    </PageLayout>
  )
}

export default Contact
